import { GP_VIEW_PERMISSION } from '@supply-mover-2-0/shared';
import type { ListBuilderConfiguration } from '../types';

/**
 * Order line items list configuration.
 *
 * Used by the Line Items tab on the Order Details page. Mirrors the
 * stacked-column layout of the original hand-coded table so the visual
 * default matches what users see today, while exposing every field through
 * the list configuration builder for per-tenant customization.
 *
 * The query function for this list (in OrderLineItemsTab) flattens a few
 * fields the underlying API returns under different shapes — `itemId` is
 * normalized from `_fk_item__id`/`fkItemId`, and `isBelowMinPrice` is
 * pre-computed so the view can do row-level highlighting.
 */
export const ORDER_DETAIL_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-order-line-items-all',
			cacheTTL: 600000,
			name: 'Order Line Items',
			type: 'default',
			active: true,
			sortOrder: 0,
			defaultSortId: 'line-number-asc',
			columns: [
				{
					id: 'line-number',
					// Header intentionally blank — the line number is self-evident,
					// and the column is sized to fit 3 digits plus cell padding.
					headers: [{ id: 'line-number-header', text: '' }],
					fields: [
						{
							id: 'line-number-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lineNumber',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
								fontWeight: 'semibold',
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'lineNumber',
							objectTable: 'order_detail',
						},
					],
					alignment: 'center',
					// The runtime table reads top-level width / minWidth / maxWidth
					// (widthConfig is only consumed by the configurator UI), so pin
					// the actual column width here. Sized for 3 digits + sort caret.
					width: 60,
					minWidth: 60,
					maxWidth: 60,
					widthConfig: { mode: 'fixed', width: 60 },
					verticalAlignment: 'middle',
				},
				{
					// Combined item column: description (linked) on line 1,
					// manufacturer on line 2, item number followed by attribute
					// icons on line 3 — mirrors the layout used by the main item
					// list (libs/core/src/list-builder/configs/item.config.ts).
					id: 'item',
					headers: [{ id: 'item-header', text: 'Item' }],
					fields: [
						{
							id: 'item-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'itemDescription',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'link' },
								fontWeight: 'semibold',
							},
							link: {
								enabled: true,
								type: 'object',
								objectTable: 'item',
								idField: 'itemId',
							},
							displayStyle: 'text',
						},
						{
							id: 'item-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm' },
							displayStyle: 'line_break',
						},
						{
							id: 'manufacturer-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'manufacturer',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'item-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm' },
							displayStyle: 'line_break',
						},
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemNumber' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							// Self-contained custom component — pulls
							// isShortDate / isRefrigerated / isHazmat /
							// effectiveDeaSchedule directly from the row.
							// Sits on the same line as itemNumber because there's
							// no line_break between them.
							id: 'item-attribute-icons',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm' },
							displayStyle: 'custom',
							customComponent: { id: 'shop-item-attribute-icons' },
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'itemDescription',
							objectTable: 'order_detail',
						},
					],
					alignment: 'left',
					minWidth: 250,
					width: 280,
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'size-strength',
					headers: [
						{ id: 'size-header', text: 'Size' },
						{ id: 'size-break', text: '', displayStyle: 'line_break' },
						{ id: 'strength-header', text: 'Strength' },
					],
					fields: [
						{
							id: 'size-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'size', objectTable: 'order_detail' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'size-strength-break',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'strength-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'strength',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					alignment: 'left',
					minWidth: 120,
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'target-cost-min',
					headers: [
						{ id: 'target-header', text: 'Target' },
						{ id: 'target-break-1', text: '', displayStyle: 'line_break' },
						{ id: 'cost-header', text: 'Cost' },
						{ id: 'target-break-2', text: '', displayStyle: 'line_break' },
						{ id: 'min-header', text: 'Min' },
					],
					fields: [
						{
							id: 'item-price-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'itemPrice',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
							},
							displayStyle: 'currency',
						},
						{
							id: 'target-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'acquisition-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'acquisitionCost',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'target-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'minimum-price-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'minimumPrice',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
					],
					sortable: false,
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'price-set',
					headers: [
						{ id: 'price-set-header', text: 'Sell Price' },
						{ id: 'price-set-break', text: '', displayStyle: 'line_break' },
						{ id: 'last-price-header', text: 'Last Price' },
					],
					fields: [
						{
							// Save icon — renders when the line price differs from
							// the item's default. Self-contained: pulls price /
							// itemPrice / minimumPrice / _fk_item__id from the row,
							// and the order's client from React context. Rendered
							// BEFORE the price so the right-aligned digits stay flush
							// to the column edge; the icon sits to the left of them
							// on the same line.
							id: 'save-price-action',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm' },
							displayStyle: 'custom',
							customComponent: { id: 'order-save-price-action' },
						},
						{
							id: 'price-set-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'price',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
							},
							displayStyle: 'currency',
						},
						{
							id: 'price-set-break',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm' },
							displayStyle: 'line_break',
						},
						{
							// Sourced from `client_item.lastPrice` joined server-side
							// in OrdersService.getOrderDetails — this is the last
							// price this client paid for the item, scoped via the
							// order's client.
							id: 'last-price-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPrice' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'price',
							objectTable: 'order_detail',
						},
					],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'quantities',
					headers: [
						{ id: 'qty-ordered-header', text: 'Ordered' },
						{ id: 'qty-break-1', text: '', displayStyle: 'line_break' },
						{ id: 'qty-shipped-header', text: 'Shipped' },
						{ id: 'qty-break-2', text: '', displayStyle: 'line_break' },
						{ id: 'qty-backordered-header', text: 'Backordered' },
					],
					fields: [
						{
							id: 'qty-ordered-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityOrdered',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
						{
							id: 'qty-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'qty-shipped-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityShipped',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
						{
							id: 'qty-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'qty-backordered-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityBackordered',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								// Mirror the legacy behaviour: warning color only when
								// there's an actual backorder. The conditional engine
								// treats null/undefined fieldValues as a no-match, so
								// empty cells fall through to defaultColor and stay
								// secondary instead of glowing yellow.
								colorRule: {
									type: 'conditional',
									rules: [
										{
											field: {
												fieldName: 'quantityBackordered',
												objectTable: 'order_detail',
											},
											operator: 'not_equals',
											value: 0,
											color: { themeColor: 'warning' },
										},
									],
									defaultColor: { themeColor: 'textSecondary' },
								},
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantityOrdered',
							objectTable: 'order_detail',
						},
					],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'subtotal',
					headers: [{ id: 'subtotal-header', text: 'Subtotal' }],
					fields: [
						{
							id: 'subtotal-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'subtotal',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
							},
							displayStyle: 'currency',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'subtotal',
							objectTable: 'order_detail',
						},
					],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'total-gp',
					headers: [
						{ id: 'total-header', text: 'Total' },
						{ id: 'total-break', text: '', displayStyle: 'line_break' },
						{ id: 'total-gp-header', text: 'Total GP' },
					],
					fields: [
						{
							id: 'total-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'total',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
							},
							displayStyle: 'currency',
						},
						{
							id: 'total-break',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'total-gp-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'totalGP',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
							requiredPermission: GP_VIEW_PERMISSION,
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'total',
							objectTable: 'order_detail',
						},
					],
					alignment: 'right',
					minWidth: 120,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
			],
			description: 'Line items for an order',
			requiredFields: [
				'order_detail._id',
				'order_detail._fk_item__id',
				'order_detail.lineNumber',
				'order_detail.price',
				'order_detail.minimumPrice',
			],
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			// Object link resolver (useListLinkNavigation) finds the entry whose
			// objectTable matches the field's `link.objectTable`; without an
			// 'item' entry here, item-detail links would fall back to this
			// order_detail baseUrl and render with a literal `:orderId` in the
			// URL because the resolver only substitutes `:id`.
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [],
		},
		{
			baseUrl: '/orders/:orderId/details/:id',
			objectLabel: 'Order Line Item',
			objectTable: 'order_detail',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'lineNumber', fieldLabel: 'Line Item' },
				{ fieldName: 'itemNumber', fieldLabel: 'Item #' },
				{ fieldName: 'itemDescription', fieldLabel: 'Description' },
				{ fieldName: 'brandEquivalent', fieldLabel: 'Brand' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'size', fieldLabel: 'Size' },
				{ fieldName: 'strength', fieldLabel: 'Strength' },
				{ fieldName: 'itemPrice', fieldLabel: 'Target Price' },
				{ fieldName: 'acquisitionCost', fieldLabel: 'Acquisition Cost' },
				{ fieldName: 'minimumPrice', fieldLabel: 'Minimum Price' },
				{ fieldName: 'price', fieldLabel: 'Sell Price' },
				{ fieldName: 'lastPrice', fieldLabel: 'Last Price' },
				{ fieldName: 'quantityOrdered', fieldLabel: 'Qty Ordered' },
				{ fieldName: 'quantityShipped', fieldLabel: 'Qty Shipped' },
				{ fieldName: 'quantityBackordered', fieldLabel: 'Qty Backordered' },
				{ fieldName: 'subtotal', fieldLabel: 'Subtotal' },
				{ fieldName: 'total', fieldLabel: 'Total' },
				{ fieldName: 'totalGP', fieldLabel: 'Total GP' },
			],
		},
	],
	tableSorts: [
		{
			id: 'line-number-asc',
			name: 'Line Number',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lineNumber',
					objectTable: 'order_detail',
				},
			],
			description: 'Sort by line number',
		},
		{
			id: 'description-asc',
			name: 'Description',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'itemDescription',
					objectTable: 'order_detail',
				},
			],
			description: 'Sort by item description',
		},
		{
			id: 'total-desc',
			name: 'Total',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'total',
					objectTable: 'order_detail',
				},
			],
			description: 'Sort by total (highest first)',
		},
	],
};
